import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, Bell, Droplet, Beef, UtensilsCrossed, Flame, TrendingUp,
  Sparkles, MoonStar, Loader2, CheckCircle2, Wheat, Salad, Dumbbell, Scale,
  BellOff,
} from "lucide-react";
import api from "@/lib/api";
import { useToast } from "@/components/Toast";
import {
  getPushSupport, getCurrentSubscription, subscribeToPush,
  unsubscribeFromPush, sendTestPush,
} from "@/lib/pushClient";

// Editable categories rendered as a list of toggles.
const CATEGORIES = [
  { key: "water_reminders",   label: "💧 Drink water",            sub: "Hydration nudges through the day",        Icon: Droplet },
  { key: "meal_reminders",    label: "🍽️ Log your meals",         sub: "Breakfast, lunch and dinner nudges",      Icon: UtensilsCrossed },
  { key: "calorie_goal",      label: "🔥 Calorie goal",           sub: "Pings near your daily cap",               Icon: Flame },
  { key: "protein_reminders", label: "💪 Protein goal",           sub: "Catch low-protein days in the afternoon", Icon: Beef },
  { key: "carb_reminders",    label: "🥖 Carb goal",              sub: "Carbs reminder if you're running low",    Icon: Wheat },
  { key: "fat_reminders",     label: "🥑 Healthy fats goal",      sub: "Don't forget good fats",                  Icon: Salad },
  { key: "workout_reminders", label: "🏋️ Complete today's workout", sub: "Afternoon nudge to train",              Icon: Dumbbell },
  { key: "weigh_in_reminders",label: "⚖️ Daily weigh-in",         sub: "Optional morning weigh-in prompt",        Icon: Scale },
  { key: "daily_summary",     label: "📊 Daily nutrition summary",sub: "Evening recap of calories, macros, water",Icon: TrendingUp },
  { key: "motivational",      label: "✨ Motivational reminders", sub: "One-line morning encouragement",          Icon: Sparkles },
  { key: "weekly_progress",   label: "📈 Weekly progress summary",sub: "End-of-week recap (Sunday by default)",   Icon: TrendingUp },
  { key: "streak_reminder",   label: "🔥 Streak reminders",       sub: "A heads-up if your streak is at risk",    Icon: Sparkles },
  { key: "congratulations",   label: "🎉 Congratulations",        sub: "Cheers when you hit goals",               Icon: CheckCircle2 },
];

// Per-category default time (paired with its toggle when relevant).
const TIME_FIELDS = [
  { key: "water_reminder_time",    label: "💧 First water reminder" },
  { key: "protein_reminder_time",  label: "💪 Protein check time" },
  { key: "carb_reminder_time",     label: "🥖 Carb check time" },
  { key: "fat_reminder_time",      label: "🥑 Fat check time" },
  { key: "workout_reminder_time",  label: "🏋️ Workout reminder time" },
  { key: "weigh_in_time",          label: "⚖️ Weigh-in time" },
  { key: "daily_summary_time",     label: "📊 Daily summary at" },
  { key: "weekly_progress_time",   label: "📈 Weekly summary at" },
];

const MEAL_FIELDS = [
  { key: "breakfast", label: "🍳 Breakfast" },
  { key: "lunch",     label: "🥗 Lunch" },
  { key: "dinner",    label: "🍽️ Dinner" },
];

function Toggle({ active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      style={{
        width: 44, height: 26, borderRadius: 13, padding: 3, cursor: "pointer",
        background: active ? "var(--gold)" : "rgba(255,255,255,0.10)",
        border: "1px solid " + (active ? "var(--gold)" : "rgba(255,255,255,0.10)"),
        position: "relative", transition: "background 160ms ease",
      }}
    >
      <div
        style={{
          width: 18, height: 18, borderRadius: 9,
          background: active ? "#000" : "var(--text-dim)",
          transform: `translateX(${active ? 18 : 0}px)`,
          transition: "transform 160ms ease, background 160ms ease",
        }}
      />
    </button>
  );
}

function TimeInput({ value, onChange, testId }) {
  return (
    <input
      type="time"
      className="input"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      style={{
        width: 100, padding: "6px 8px", borderRadius: 8,
        background: "var(--surface-soft)", color: "var(--text)",
        border: "1px solid var(--line)", fontSize: 13,
      }}
    />
  );
}

export default function RemindersSettingsScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving]     = useState(false);

  // Push state
  const [pushSupport, setPushSupport] = useState(getPushSupport());
  const [pushSub, setPushSub]         = useState(null);
  const [pushBusy, setPushBusy]       = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reminders/settings");
        setSettings(data?.settings || null);
      } catch {
        toast.show("Couldn't load reminder settings", "error");
      }
    })();
    // Detect existing push subscription on mount.
    (async () => {
      try {
        const sub = await getCurrentSubscription();
        setPushSub(sub);
      } catch { /* ignore */ }
      setPushSupport(getPushSupport());
    })();
  }, [toast]);

  const save = async (next) => {
    setSettings(next);
    setSaving(true);
    try {
      await api.put("/reminders/settings", next);
    } catch {
      toast.show("Couldn't save. Try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const sub = await subscribeToPush();
      setPushSub(sub);
      setPushSupport(getPushSupport());
      toast.show("Push notifications enabled", "success");
    } catch (err) {
      toast.show(err?.message || "Couldn't enable push notifications.", "error");
    } finally {
      setPushBusy(false);
    }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      setPushSub(null);
      setPushSupport(getPushSupport());
      toast.show("Push notifications turned off", "info");
    } catch {
      toast.show("Couldn't disable push.", "error");
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    setPushBusy(true);
    try {
      await sendTestPush();
      toast.show("Test sent — check your notifications.", "success");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send test.";
      toast.show(msg, "error");
    } finally {
      setPushBusy(false);
    }
  };

  if (!settings) {
    return (
      <div className="screen with-nav fade-in" data-testid="reminders-settings-loading">
        <div className="top-bar">
          <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Back">
            <ChevronLeft size={18} />
          </button>
          <div className="logo-mark">REMINDERS</div>
          <span style={{ width: 32 }} />
        </div>
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Loader2 size={20} className="spin" />
        </div>
      </div>
    );
  }

  const setKey = (k, v) => save({ ...settings, [k]: v });
  const setMealTime = (mk, v) => save({
    ...settings,
    meal_times: { ...(settings.meal_times || {}), [mk]: v },
  });

  return (
    <div className="screen with-nav fade-in" data-testid="reminders-settings-screen">
      <div className="top-bar">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Back" data-testid="reminders-back">
          <ChevronLeft size={18} />
        </button>
        <div className="logo-mark">REMINDERS</div>
        <span style={{ width: 32 }} />
      </div>

      <div className="eyebrow" style={{ marginTop: 16, marginBottom: 8 }}>YOUR NUDGES</div>
      <h2 className="h2" style={{ fontSize: 22, marginBottom: 6 }}>
        Reminders, dialled in.
      </h2>
      <p className="body-sm" style={{ color: "var(--text-dim)", marginBottom: 18 }}>
        Personalised by your goals + your day. Toggle anything you don&apos;t want.
      </p>

      {/* Master switch */}
      <div className="outfit-section" data-testid="reminders-master-section">
        <div className="tile" style={{ flexDirection: "row", alignItems: "center", padding: 14, gap: 12 }}>
          <div className="tile-icon"><Bell size={18} strokeWidth={1.8} /></div>
          <div style={{ flex: 1 }}>
            <div className="h3" style={{ color: "var(--text)" }}>All reminders</div>
            <div className="tiny" style={{ marginTop: 4, color: "var(--text-dim)" }}>
              Master switch — turn everything off in one tap.
            </div>
          </div>
          <Toggle
            active={!!settings.enabled}
            onClick={() => setKey("enabled", !settings.enabled)}
            testId="reminders-toggle-master"
          />
        </div>
      </div>

      {/* PUSH NOTIFICATIONS — works even when the app is closed */}
      <div className="outfit-section" data-testid="reminders-push-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>PUSH NOTIFICATIONS</div>
        <div className="tile" style={{ flexDirection: "row", alignItems: "flex-start", padding: 14, gap: 12 }}>
          <div className="tile-icon">
            {pushSub ? <Bell size={18} strokeWidth={1.8} /> : <BellOff size={18} strokeWidth={1.8} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="h3" style={{ color: "var(--text)" }}>
              {pushSub ? "Push notifications are ON" : "Get reminders when the app is closed"}
            </div>
            <div className="tiny" style={{ marginTop: 4, color: "var(--text-dim)", lineHeight: 1.5 }}>
              {!pushSupport.supported && pushSupport.iosRequiresPwa && (
                <>To enable on iPhone: tap Share → <strong>Add to Home Screen</strong>, then open Nova from there.</>
              )}
              {!pushSupport.supported && !pushSupport.iosRequiresPwa && (
                <>Push isn&apos;t supported by this browser. Try Chrome, Firefox, or installed iOS Safari (16.4+).</>
              )}
              {pushSupport.supported && !pushSub && (
                <>Reminders for water, meals, workouts, and goals will arrive even if Nova is closed.</>
              )}
              {pushSupport.supported && pushSub && (
                <>You&apos;ll receive notifications based on your schedule below. Tap test to verify.</>
              )}
            </div>
            {pushSupport.supported && (
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                {!pushSub ? (
                  <button
                    type="button"
                    onClick={enablePush}
                    disabled={pushBusy}
                    data-testid="push-enable-btn"
                    className="btn btn-gold"
                    style={{ padding: "8px 14px", fontSize: 13 }}
                  >
                    {pushBusy ? <Loader2 size={14} className="spin" /> : <Bell size={14} />}
                    Enable push
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={testPush}
                      disabled={pushBusy}
                      data-testid="push-test-btn"
                      className="btn btn-outline"
                      style={{ padding: "8px 14px", fontSize: 13 }}
                    >
                      {pushBusy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />}
                      Send test
                    </button>
                    <button
                      type="button"
                      onClick={disablePush}
                      disabled={pushBusy}
                      data-testid="push-disable-btn"
                      className="btn btn-outline"
                      style={{ padding: "8px 14px", fontSize: 13 }}
                    >
                      <BellOff size={14} />
                      Turn off
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category list */}
      <div
        className="outfit-section"
        data-testid="reminders-categories-section"
        style={{ opacity: settings.enabled ? 1 : 0.45, pointerEvents: settings.enabled ? "auto" : "none" }}
      >
        <div className="eyebrow" style={{ marginBottom: 12 }}>CATEGORIES</div>
        {CATEGORIES.map(({ key, label, sub, Icon }) => (
          <div
            key={key}
            className="tile"
            data-testid={`reminders-row-${key}`}
            style={{ flexDirection: "row", alignItems: "center", padding: 14, gap: 12 }}
          >
            <div className="tile-icon"><Icon size={18} strokeWidth={1.8} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="h3" style={{ color: "var(--text)" }}>{label}</div>
              <div className="tiny" style={{ marginTop: 4, color: "var(--text-dim)" }}>{sub}</div>
            </div>
            <Toggle
              active={!!settings[key]}
              onClick={() => setKey(key, !settings[key])}
              testId={`reminders-toggle-${key}`}
            />
          </div>
        ))}
      </div>

      {/* Meal times */}
      <div
        className="outfit-section"
        data-testid="reminders-meal-times-section"
        style={{ opacity: settings.enabled && settings.meal_reminders ? 1 : 0.45 }}
      >
        <div className="eyebrow" style={{ marginBottom: 12 }}>MEAL REMINDER TIMES</div>
        {MEAL_FIELDS.map(({ key, label }) => (
          <div
            key={key}
            className="tile"
            data-testid={`reminders-meal-time-${key}`}
            style={{ flexDirection: "row", alignItems: "center", padding: 14, gap: 12 }}
          >
            <div style={{ flex: 1 }}>
              <div className="h3" style={{ color: "var(--text)" }}>{label}</div>
            </div>
            <TimeInput
              value={(settings.meal_times || {})[key] || ""}
              onChange={(v) => setMealTime(key, v)}
              testId={`reminders-meal-time-input-${key}`}
            />
          </div>
        ))}
      </div>

      {/* Other times */}
      <div
        className="outfit-section"
        data-testid="reminders-times-section"
        style={{ opacity: settings.enabled ? 1 : 0.45 }}
      >
        <div className="eyebrow" style={{ marginBottom: 12 }}>OTHER TIMES</div>
        {TIME_FIELDS.map(({ key, label }) => (
          <div
            key={key}
            className="tile"
            data-testid={`reminders-time-${key}`}
            style={{ flexDirection: "row", alignItems: "center", padding: 14, gap: 12 }}
          >
            <div style={{ flex: 1 }}>
              <div className="h3" style={{ color: "var(--text)" }}>{label}</div>
            </div>
            <TimeInput
              value={settings[key] || ""}
              onChange={(v) => setKey(key, v)}
              testId={`reminders-time-input-${key}`}
            />
          </div>
        ))}
      </div>

      {/* Quiet hours */}
      <div className="outfit-section" data-testid="reminders-quiet-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>QUIET HOURS</div>
        <div className="tile" style={{ flexDirection: "row", alignItems: "center", padding: 14, gap: 12 }}>
          <div className="tile-icon"><MoonStar size={18} strokeWidth={1.8} /></div>
          <div style={{ flex: 1 }}>
            <div className="h3" style={{ color: "var(--text)" }}>Do-not-disturb window</div>
            <div className="tiny" style={{ marginTop: 4, color: "var(--text-dim)" }}>
              No reminders fire between these times.
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <TimeInput
              value={settings.quiet_hours_start || ""}
              onChange={(v) => setKey("quiet_hours_start", v)}
              testId="reminders-quiet-start"
            />
            <span className="tiny" style={{ color: "var(--text-faint)" }}>to</span>
            <TimeInput
              value={settings.quiet_hours_end || ""}
              onChange={(v) => setKey("quiet_hours_end", v)}
              testId="reminders-quiet-end"
            />
          </div>
        </div>
      </div>

      <div className="tiny" style={{ marginTop: 18, textAlign: "center", color: "var(--text-faint)", lineHeight: 1.55 }}>
        {saving ? "Saving…" : "All changes save automatically."}
      </div>
    </div>
  );
}
